import type { Budget, Grant } from "../../services/financialService";
import { StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/** Views, sorts and columns for the budgets and grants registers. */

const money = (value: string | number) => Number(value || 0).toLocaleString();

/** The approval step a budget or grant is at, in words. */
export function approvalStage(record: { workflow_status?: string; status?: string }) {
  switch (record.workflow_status) {
    case "draft":
      return { key: "draft", label: "Draft", dot: "bg-gray-400" };
    case "returned":
      return { key: "returned", label: "Returned for changes", dot: "bg-amber-500" };
    case "submitted":
      return { key: "submitted", label: "Awaiting review", dot: "bg-sky-500" };
    case "verified":
      return { key: "verified", label: "Awaiting approval", dot: "bg-indigo-500" };
    case "rejected":
      return { key: "rejected", label: "Rejected", dot: "bg-red-500" };
    default:
      return record.status === "closed"
        ? { key: "closed", label: "Closed", dot: "bg-gray-400" }
        : { key: "approved", label: "Approved", dot: "bg-emerald-500" };
  }
}

const stageViews = <T extends { workflow_status?: string; status?: string }>() => [
  { key: "all", label: "All" },
  { key: "preparing", label: "Drafts & returned", match: (row: T) => ["draft", "returned"].includes(row.workflow_status ?? "") },
  { key: "review", label: "Awaiting review", match: (row: T) => row.workflow_status === "submitted" },
  { key: "approval", label: "Awaiting approval", match: (row: T) => row.workflow_status === "verified" },
  { key: "approved", label: "Approved", match: (row: T) => approvalStage(row).key === "approved" },
];

// Budgets ---------------------------------------------------------------------------

export const budgetTotal = (budget: Budget) => budget.lines.reduce((sum, line) => sum + Number(line.amount), 0);

export const budgetConfig: RegisterConfig<Budget> = {
  search: (budget) => [budget.name, budget.prepared_by_name, ...budget.lines.map((line) => `${line.account_code} ${line.account_name} ${line.notes}`)].join(" "),
  views: stageViews<Budget>(),
  date: { label: "Starts", value: (budget) => budget.start_date },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<Budget>((budget) => budget.start_date)) },
    { key: "start", label: "Oldest first", compare: byText<Budget>((budget) => budget.start_date) },
    { key: "-total", label: "Total (highest first)", compare: reversed(byNumber<Budget>(budgetTotal)) },
    { key: "name", label: "Name (A–Z)", compare: byText<Budget>((budget) => budget.name) },
  ],
};

export const budgetColumns: RegisterColumn<Budget>[] = [
  { header: "Budget", cell: (budget) => <span className="font-medium text-gray-900">{budget.name}</span>, exportValue: (budget) => budget.name },
  { header: "Period", cell: (budget) => `${budget.start_date} → ${budget.end_date}`, exportValue: (budget) => `${budget.start_date} to ${budget.end_date}`, className: "tabular-nums text-gray-600" },
  { header: "Accounts", cell: (budget) => budget.lines.length, exportValue: (budget) => budget.lines.length, align: "right" },
  { header: "Budgeted (UGX)", cell: (budget) => <span className="font-semibold text-gray-900">{money(budgetTotal(budget))}</span>, exportValue: budgetTotal, align: "right" },
  { header: "Prepared by", cell: (budget) => budget.prepared_by_name || "—", exportValue: (budget) => budget.prepared_by_name ?? "", className: "text-gray-600" },
  { header: "Stage", cell: (budget) => <StatusDot {...approvalStage(budget)} />, exportValue: (budget) => approvalStage(budget).label },
];

// Grants -----------------------------------------------------------------------------

export const restrictionLabel = (restriction: Grant["restriction"]) => humanise(restriction);

export const grantConfig: RegisterConfig<Grant> = {
  search: (grant) => [grant.name, grant.donor, grant.project, grant.notes, grant.prepared_by_name].join(" "),
  views: [
    ...stageViews<Grant>().slice(0, 4),
    { key: "active", label: "Active", match: (grant) => approvalStage(grant).key === "approved" },
    { key: "closed", label: "Closed or rejected", match: (grant) => ["closed", "rejected"].includes(approvalStage(grant).key) },
  ],
  date: { label: "Funding starts", value: (grant) => grant.funding_period_start },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<Grant>((grant) => grant.funding_period_start)) },
    { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<Grant>((grant) => grant.balance)) },
    { key: "-total", label: "Committed (highest first)", compare: reversed(byNumber<Grant>((grant) => grant.total_amount)) },
    { key: "donor", label: "Donor (A–Z)", compare: byText<Grant>((grant) => grant.donor) },
  ],
};

export const grantColumns: RegisterColumn<Grant>[] = [
  { header: "Grant", cell: (grant) => <span className="font-medium text-gray-900">{grant.name}</span>, exportValue: (grant) => grant.name, className: "max-w-[14rem] truncate" },
  { header: "Donor", cell: (grant) => grant.donor, exportValue: (grant) => grant.donor, className: "max-w-[12rem] truncate text-gray-700" },
  { header: "Restriction", cell: (grant) => restrictionLabel(grant.restriction), exportValue: (grant) => restrictionLabel(grant.restriction), className: "text-gray-600" },
  { header: "Funding period", cell: (grant) => `${grant.funding_period_start} → ${grant.funding_period_end}`, exportValue: (grant) => `${grant.funding_period_start} to ${grant.funding_period_end}`, className: "tabular-nums text-gray-600" },
  { header: "Committed (UGX)", cell: (grant) => money(grant.total_amount), exportValue: (grant) => Number(grant.total_amount), align: "right" },
  { header: "Received (UGX)", cell: (grant) => money(grant.received_amount), exportValue: (grant) => Number(grant.received_amount), align: "right" },
  { header: "Spent (UGX)", cell: (grant) => money(grant.spent_amount), exportValue: (grant) => Number(grant.spent_amount), align: "right" },
  { header: "Balance (UGX)", cell: (grant) => <span className="font-semibold text-gray-900">{money(grant.balance)}</span>, exportValue: (grant) => Number(grant.balance), align: "right" },
  { header: "Stage", cell: (grant) => <StatusDot {...approvalStage(grant)} />, exportValue: (grant) => approvalStage(grant).label },
];
